/**
 * sl-ht07.8: Workspace Snapshot Export
 *
 * Web-compatible full workspace export using ZipTransformer.
 * Works in both browser and Electron (unlike DesktopExportPanel which is Electron-only).
 */
import { notify } from '@affine/component';
import { SettingRow } from '@affine/component/setting-components';
import { Button } from '@affine/component/ui/button';
import { DocsService } from '@affine/core/modules/doc';
import { exportWorkspaceSnapshotStream } from '@affine/core/modules/doc/services/workspace-snapshot-stream';
import { AccessibleDocCacheStore } from '@affine/core/modules/doc/stores/accessible-doc-cache';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { ExportIcon } from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import { useCallback, useRef, useState } from 'react';

export const SnapshotExportPanel = () => {
  const t = useI18n();
  const workspaceService = useService(WorkspaceService);
  const docsService = useService(DocsService);
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const abortRef = useRef<AbortController | null>(null);
  const [phase, setPhase] = useState<string | null>(null);

  const onExport = useCallback(async () => {
    const abort = new AbortController();
    abortRef.current = abort;
    setPhase('Preparing document inventory…');
    try {
      await exportWorkspaceSnapshotStream({
        workspaceService,
        docsService,
        accessibleDocCache,
        signal: abort.signal,
        onProgress: progress => {
          if (progress.phase === 'inventory') {
            setPhase(`Discovered ${progress.discoveredDocs} documents…`);
          } else if (progress.phase === 'documents') {
            setPhase(`Writing document ${progress.processedDocs}…`);
          } else if (progress.phase === 'assets') {
            setPhase(`Writing file ${progress.processedAssets}…`);
          } else {
            setPhase('Finalizing snapshot…');
          }
        },
      });
      notify.success({
        title: t['com.streamlain.export.success.title'](),
        message: t['com.streamlain.export.success.message'](),
      });
    } catch (error) {
      if (!abort.signal.aborted) {
        console.error(error);
        notify.error({
          title: t['com.streamlain.export.error.title'](),
          message: t['com.streamlain.export.error.message'](),
        });
      }
    } finally {
      abortRef.current = null;
      setPhase(null);
    }
  }, [accessibleDocCache, docsService, t, workspaceService]);

  return (
    <SettingRow
      name="Snapshot Export"
      desc={
        phase ??
        'Export document content, databases, and referenced files as a ZIP. Folder organization and unused files are not included.'
      }
    >
      {phase ? (
        <Button variant="secondary" onClick={() => abortRef.current?.abort()}>
          Cancel
        </Button>
      ) : (
        <Button
          variant="primary"
          data-testid="export-workspace-snapshot"
          onClick={() => void onExport()}
          prefix={<ExportIcon />}
        >
          Export Workspace
        </Button>
      )}
    </SettingRow>
  );
};
